// ==========================================================================
// Auth Hooks - React Query hooks for OTP authentication
// ==========================================================================

import { useMutation } from '@tanstack/react-query';
import { AxiosError } from 'axios';
import { sendOtp, verifyOtp, resendOtp } from '@/services/auth.service';
import { identifyById } from '@/lib/mixpanel';
import type { OTPSendRequest, OTPVerifyRequest } from '@/types/auth.types';

/**
 * Hook to send OTP
 */
export function useSendOtp() {
  return useMutation({
    mutationFn: (data: OTPSendRequest) => sendOtp(data),
  });
}

/**
 * Hook to verify OTP - stores access_token on success
 */
export function useVerifyOtp() {
  return useMutation({
    mutationFn: (data: OTPVerifyRequest) => verifyOtp(data),
    onSuccess: (data) => {
      if (data.access_token) {
        localStorage.setItem('access_token', data.access_token);
      }
      // Identify in Mixpanel immediately so onboarding events use the user id
      // rather than the anonymous $device:... id. Full profile is set later
      // by useAnalytics when the user object is fetched.
      if (data.user?.id) {
        identifyById(data.user.id);
      }
    },
  });
}

/**
 * Hook to resend OTP
 */
export function useResendOtp() {
  return useMutation({
    mutationFn: (data: OTPSendRequest) => resendOtp(data),
  });
}

/**
 * Extract cooldown seconds from a 429 rate-limit error
 */
export function getCooldownFromError(error: unknown): number | null {
  if (error instanceof AxiosError && error.response?.status === 429) {
    return error.response.data?.cooldown ?? null;
  }
  return null;
}
